import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { leadsAPI } from '../services/api';
import { formatDate, formatCurrency } from '../utils/currency';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Users, 
  Phone, Mail, ArrowLeft, Flame, Clock, XCircle, CheckCircle,
  UserPlus, PhoneCall, AlertCircle, MapPin, Eye, Edit, Building,
  Trash2, RefreshCw, ArrowRightCircle, DollarSign, User, Hash
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import useFinancialVisibility from '../hooks/useFinancialVisibility';

const MonthlyLeads = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const hideFinancials = useFinancialVisibility();
  
  // Get initial month from URL or current month
  const getInitialDate = () => {
    const monthParam = searchParams.get('month');
    if (monthParam) {
      const [year, month] = monthParam.split('-').map(Number);
      return new Date(year, month - 1, 1);
    }
    return new Date();
  };
  
  const [currentDate, setCurrentDate] = useState(getInitialDate());
  const [leads, setLeads] = useState([]);
  const [venues, setVenues] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateLeads, setSelectedDateLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetailOpen, setLeadDetailOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [datesNotSureOpen, setDatesNotSureOpen] = useState(false);

  const loadLeads = useCallback(async () => {
    setIsLoading(true);
    try {
      const [leadsData, venuesData, usersData] = await Promise.all([
        leadsAPI.getAll(),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/venues`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json()).catch(() => []),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json()).catch(() => [])
      ]);
      setLeads(leadsData || []);
      setVenues(venuesData || []);
      setUsers(usersData || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load leads', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  // Get user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || user?.email || 'Unknown';
  };

  // Get user role by ID
  const getUserRole = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.role || 'staff';
  };

  // Get venue name by ID
  const getVenueName = (venueId) => {
    const venue = venues.find(v => v.id === venueId);
    return venue?.name || 'Not Assigned';
  };

  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    if (!selectedLead) return;
    
    setUpdatingStatus(true);
    try {
      await leadsAPI.update(selectedLead.id, { ...selectedLead, status: newStatus });
      toast({ title: 'Success', description: `Lead status updated to ${newStatus}` });
      
      // Update local state
      setSelectedLead({ ...selectedLead, status: newStatus });
      setLeads(leads.map(l => l.id === selectedLead.id ? { ...l, status: newStatus } : l));
      
      // Update selected date leads if dialog is open
      if (selectedDate) {
        setSelectedDateLeads(prev => prev.map(l => l.id === selectedLead.id ? { ...l, status: newStatus } : l));
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle delete lead
  const handleDeleteLead = async () => {
    if (!selectedLead) return;
    
    if (!window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      return;
    }
    
    try {
      await leadsAPI.delete(selectedLead.id);
      toast({ title: 'Success', description: 'Lead deleted successfully' });
      
      // Update local state
      setLeads(leads.filter(l => l.id !== selectedLead.id));
      setSelectedDateLeads(prev => prev.filter(l => l.id !== selectedLead.id));
      setLeadDetailOpen(false);
      
      if (selectedDateLeads.length <= 1) {
        setDialogOpen(false);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete lead', variant: 'destructive' });
    }
  };

  // Get leads for a specific date - ONLY based on bookingDateFrom
  const getLeadsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return leads.filter(lead => lead.bookingDateFrom === dateStr);
  };

  // Get leads count for a specific date
  const getLeadsCountForDate = (date) => {
    return getLeadsForDate(date).length;
  };

  // Get leads without booking date ("Dates Not Sure")
  const getLeadsWithoutBookingDate = () => {
    return leads.filter(lead => !lead.bookingDateFrom);
  };

  // Check if a lead's event date has passed
  const isDatePassed = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(dateStr);
    return eventDate < today;
  };

  // Get status badge color
  const getStatusColor = (status, bookingDateFrom) => {
    // If date has passed and not converted/lost, show as lost
    if (isDatePassed(bookingDateFrom) && !['converted', 'lost'].includes(status)) {
      return 'bg-gray-50 dark:bg-secondary0';
    }
    
    switch (status) {
      case 'new': return 'bg-blue-50 dark:bg-blue-900/200';
      case 'contacted': return 'bg-yellow-50 dark:bg-yellow-900/200';
      case 'qualified': return 'bg-purple-50 dark:bg-purple-900/200';
      case 'negotiating': return 'bg-orange-50 dark:bg-orange-900/200';
      case 'converted': return 'bg-green-50 dark:bg-green-900/200';
      case 'lost': return 'bg-red-50 dark:bg-red-900/200';
      case 'cold': return 'bg-cyan-50 dark:bg-cyan-900/200';
      default: return 'bg-gray-50 dark:bg-secondary0';
    }
  };

  // Get status label
  const getStatusLabel = (status, bookingDateFrom) => {
    if (isDatePassed(bookingDateFrom) && !['converted', 'lost'].includes(status)) {
      return 'Lost (Expired)';
    }
    
    const labels = {
      'new': 'New',
      'contacted': 'Contacted',
      'qualified': 'Qualified',
      'negotiating': 'Negotiating',
      'converted': 'Converted',
      'lost': 'Lost',
      'cold': 'Cold'
    };
    return labels[status] || status;
  };

  // Get status color for badges (returns bg and text classes)
  const getStatusColorText = (status) => {
    const configs = {
      'new': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700' },
      'contacted': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700' },
      'qualified': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700' },
      'in_followup': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700' },
      'negotiating': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700' },
      'converted': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700' },
      'won': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700' },
      'lost': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700' },
      'cold': { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-700' }
    };
    return configs[status] || { bg: 'bg-gray-100 dark:bg-secondary', text: 'text-gray-700 dark:text-foreground' };
  };

  // Get status icon
  const getStatusIcon = (status, bookingDateFrom) => {
    if (isDatePassed(bookingDateFrom) && !['converted', 'lost'].includes(status)) {
      return <XCircle className="w-3 h-3" />;
    }
    
    switch (status) {
      case 'new': return <UserPlus className="w-3 h-3" />;
      case 'contacted': return <PhoneCall className="w-3 h-3" />;
      case 'qualified': return <CheckCircle className="w-3 h-3" />;
      case 'negotiating': return <Clock className="w-3 h-3" />;
      case 'converted': return <CheckCircle className="w-3 h-3" />;
      case 'lost': return <XCircle className="w-3 h-3" />;
      case 'cold': return <AlertCircle className="w-3 h-3" />;
      default: return null;
    }
  };

  // Handle date click
  const handleDateClick = (date) => {
    const leadsForDate = getLeadsForDate(date);
    if (leadsForDate.length > 0) {
      setSelectedDate(date);
      setSelectedDateLeads(leadsForDate);
      setDialogOpen(true);
    }
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  // Get month stats - ONLY based on bookingDateFrom
  const getMonthStats = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Filter leads with bookingDateFrom in current month
    const monthLeads = leads.filter(lead => {
      if (!lead.bookingDateFrom) return false;
      const leadDate = new Date(lead.bookingDateFrom);
      return leadDate.getFullYear() === year && leadDate.getMonth() === month;
    });

    const total = monthLeads.length;
    const newLeads = monthLeads.filter(l => l.status === 'new').length;
    const converted = monthLeads.filter(l => l.status === 'converted' || l.status === 'won').length;
    const lost = monthLeads.filter(l => l.status === 'lost' || (l.bookingDateFrom && isDatePassed(l.bookingDateFrom) && !['converted', 'won', 'lost'].includes(l.status))).length;

    return { total, newLeads, converted, lost };
  };

  const stats = getMonthStats();
  const leadsWithoutDate = getLeadsWithoutBookingDate();
  const calendarDays = generateCalendarDays();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/leads')} className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-foreground">Month Wise Leads</h1>
          <p className="text-xs text-gray-500 dark:text-muted-foreground">View leads enquiries by booking date</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
          <CardContent className="p-3 text-center">
            <Users className="w-5 h-5 mx-auto mb-1 text-blue-600" />
            <p className="text-lg sm:text-xl font-bold text-blue-700">{stats.total}</p>
            <p className="text-[10px] sm:text-xs text-blue-600">Total Leads</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200">
          <CardContent className="p-3 text-center">
            <Flame className="w-5 h-5 mx-auto mb-1 text-purple-600" />
            <p className="text-lg sm:text-xl font-bold text-purple-700">{stats.newLeads}</p>
            <p className="text-[10px] sm:text-xs text-purple-600">New Leads</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200">
          <CardContent className="p-3 text-center">
            <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-600" />
            <p className="text-lg sm:text-xl font-bold text-green-700">{stats.converted}</p>
            <p className="text-[10px] sm:text-xs text-green-600">Converted</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 dark:bg-red-900/20 border-red-200">
          <CardContent className="p-3 text-center">
            <XCircle className="w-5 h-5 mx-auto mb-1 text-red-600" />
            <p className="text-lg sm:text-xl font-bold text-red-700">{stats.lost}</p>
            <p className="text-[10px] sm:text-xs text-red-600">Lost/Expired</p>
          </CardContent>
        </Card>
      </div>

      {/* Dates Not Sure Card - Always visible */}
      <Card 
        className="mb-4 bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setDatesNotSureOpen(true)}
        data-testid="dates-not-sure-card"
      >
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-50 dark:bg-orange-900/200 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-orange-800">Dates Not Sure</p>
                  <p className="text-xs text-orange-600">{leadsWithoutDate.length} lead{leadsWithoutDate.length !== 1 ? 's' : ''} without booking date</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-orange-500" />
            </div>
          </CardContent>
        </Card>

      {/* Calendar Card */}
      <Card>
        <CardHeader className="p-3 sm:p-4 pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={goToPreviousMonth} className="p-2">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <CardTitle className="text-base sm:text-lg flex items-center gap-2 dark:text-foreground">
              <CalendarIcon className="w-5 h-5 text-teal-600" />
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={goToNextMonth} className="p-2">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-2 sm:p-4">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-muted-foreground py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="aspect-square" />;
              }

              const leadsCount = getLeadsCountForDate(day);
              const isToday = day.toDateString() === new Date().toDateString();
              const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));
              const hasLeads = leadsCount > 0;

              return (
                <div
                  key={day.toISOString()}
                  onClick={() => handleDateClick(day)}
                  className={`
                    aspect-square p-1 rounded-lg border transition-all
                    ${isToday ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-200 dark:border-border'}
                    ${hasLeads ? 'cursor-pointer hover:bg-gray-100 dark:bg-secondary' : ''}
                    ${isPast && !isToday ? 'bg-gray-50 dark:bg-secondary' : 'bg-white dark:bg-card'}
                  `}
                >
                  <div className="h-full flex flex-col">
                    <span className={`text-[10px] sm:text-xs font-medium ${isToday ? 'text-teal-700' : isPast ? 'text-gray-400 dark:text-muted-foreground' : 'text-gray-700 dark:text-foreground'}`}>
                      {day.getDate()}
                    </span>
                    {hasLeads && (
                      <div className="flex-1 flex items-center justify-center">
                        <Badge className={`text-[8px] sm:text-[10px] px-1 py-0 ${isPast && !hasLeads ? 'bg-gray-400' : 'bg-teal-600'}`}>
                          {leadsCount}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t justify-center text-[10px] sm:text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-teal-600" />
              <span className="text-gray-600 dark:text-muted-foreground">Has Leads</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded border-2 border-teal-500" />
              <span className="text-gray-600 dark:text-muted-foreground">Today</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-gray-200" />
              <span className="text-gray-600 dark:text-muted-foreground">Past Date</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads Overview Dialog - Shows list of leads for selected date */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[95vw] max-w-lg mx-auto p-4 max-h-[85vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-teal-600" />
              Leads for {selectedDate ? formatDate(selectedDate.toISOString().split('T')[0]) : ''}
              <Badge className="ml-2 bg-teal-600">{selectedDateLeads.length}</Badge>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {selectedDateLeads.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-muted-foreground py-4">No leads for this date</p>
            ) : (
              selectedDateLeads.map(lead => (
                <Card 
                  key={lead.id} 
                  data-testid={`lead-card-${lead.id}`}
                  className={`cursor-pointer hover:shadow-md hover:border-teal-300 transition-all ${isDatePassed(lead.bookingDateFrom) && !['converted', 'lost'].includes(lead.status) ? 'border-red-200 bg-red-50 dark:bg-red-900/20' : ''}`}
                  onClick={() => {
                    setSelectedLead(lead);
                    setLeadDetailOpen(true);
                  }}
                >
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold text-sm sm:text-base flex items-center gap-2">
                          {lead.name}
                          <Eye className="w-3 h-3 text-gray-400 dark:text-muted-foreground" />
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-muted-foreground mt-1">
                          <Phone className="w-3 h-3" />
                          <span>{lead.phone}</span>
                        </div>
                      </div>
                      <Badge className={`${getStatusColor(lead.status, lead.bookingDateFrom)} text-white text-[10px] flex items-center gap-1`}>
                        {getStatusIcon(lead.status, lead.bookingDateFrom)}
                        {getStatusLabel(lead.status, lead.bookingDateFrom)}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-muted-foreground mt-2 pt-2 border-t">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        {lead.eventType || 'Event'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {lead.expectedGuests || '-'} guests
                      </span>
                      {!hideFinancials && lead.budget && (
                        <span className="flex items-center gap-1 text-green-600 font-medium">
                          {formatCurrency(lead.budget)}
                        </span>
                      )}
                    </div>
                    
                    {isDatePassed(lead.bookingDateFrom) && !['converted', 'lost'].includes(lead.status) && (
                      <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 rounded text-xs text-red-700 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Event date has passed - Lead marked as Lost
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          
          <p className="text-xs text-center text-gray-400 dark:text-muted-foreground mt-2">Click on a lead to view complete details</p>
        </DialogContent>
      </Dialog>

      {/* Lead Complete Details Dialog - Matching Image Design */}
      <Dialog open={leadDetailOpen} onOpenChange={setLeadDetailOpen}>
        <DialogContent className="w-[95vw] max-w-lg mx-auto p-0 max-h-[90vh] overflow-hidden">
          {selectedLead && (
            <div className="flex flex-col h-full max-h-[90vh]">
              {/* Header with Name and Badges */}
              <div className="p-4 border-b bg-white dark:bg-card sticky top-0 z-10">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-foreground truncate">{selectedLead.name}</h2>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {/* Status Badge */}
                      <Badge className={`${getStatusColor(selectedLead.status, selectedLead.bookingDateFrom)} text-white text-[10px] px-2 py-0.5`}>
                        {getStatusLabel(selectedLead.status, selectedLead.bookingDateFrom)}
                      </Badge>
                      {/* Priority Badge */}
                      <Badge className={`text-[10px] px-2 py-0.5 ${
                        selectedLead.priority === 'hot' ? 'bg-red-50 dark:bg-red-900/200 text-white' : 
                        selectedLead.priority === 'warm' ? 'bg-orange-50 dark:bg-orange-900/200 text-white' : 
                        'bg-blue-100 dark:bg-blue-900/30 text-blue-700'
                      }`}>
                        {selectedLead.priority === 'hot' ? '🔥 Hot' : 
                         selectedLead.priority === 'warm' ? '🌡️ Warm' : '❄️ Cold'}
                      </Badge>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0"
                    onClick={() => setLeadDetailOpen(false)}
                  >
                    <XCircle className="w-5 h-5 text-gray-400 dark:text-muted-foreground" />
                  </Button>
                </div>
                
                {/* Lead ID */}
                <div className="flex items-center gap-1 mt-2 text-xs text-gray-500 dark:text-muted-foreground">
                  <Hash className="w-3 h-3" />
                  <span className="font-mono">{selectedLead.id?.slice(0, 8)}...</span>
                </div>
              </div>
              
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Contact Info Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-secondary rounded-lg">
                    <div className="w-8 h-8 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center shrink-0">
                      <Phone className="w-4 h-4 text-teal-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-gray-500 dark:text-muted-foreground uppercase">Phone</p>
                      <a href={`tel:${selectedLead.phone}`} className="text-sm font-medium text-teal-600 hover:underline">
                        {selectedLead.phone}
                      </a>
                    </div>
                  </div>
                  
                  {selectedLead.email && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-secondary rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center shrink-0">
                        <Mail className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-gray-500 dark:text-muted-foreground uppercase">Email</p>
                        <a href={`mailto:${selectedLead.email}`} className="text-sm font-medium text-blue-600 hover:underline truncate block">
                          {selectedLead.email}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Event Details Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-[10px] text-purple-600 uppercase font-medium">Event Type</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-foreground mt-0.5">{selectedLead.eventType || '-'}</p>
                  </div>
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-[10px] text-purple-600 uppercase font-medium">Booking Date</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-foreground mt-0.5">
                      {selectedLead.bookingDateFrom ? formatDate(selectedLead.bookingDateFrom) : '-'}
                      {selectedLead.bookingDateTo && selectedLead.bookingDateTo !== selectedLead.bookingDateFrom && (
                        <span className="text-xs text-gray-500 dark:text-muted-foreground"> - {formatDate(selectedLead.bookingDateTo)}</span>
                      )}
                    </p>
                  </div>
                </div>
                
                {/* Follow-up & Guests Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <p className="text-[10px] text-orange-600 uppercase font-medium">Follow-up Date</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-foreground mt-0.5">
                      {selectedLead.followupDate ? formatDate(selectedLead.followupDate) : '-'}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-[10px] text-blue-600 uppercase font-medium">Guests</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-foreground mt-0.5 flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {selectedLead.expectedGuests || '-'}
                    </p>
                  </div>
                </div>
                
                {/* Budget - Highlighted (Hidden when financials are hidden) */}
                {!hideFinancials && (
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                    <p className="text-[10px] text-green-600 uppercase font-medium flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      Budget
                    </p>
                    <p className="text-xl font-bold text-green-700 mt-1">
                      {selectedLead.budget ? formatCurrency(selectedLead.budget) : '₹0'}
                    </p>
                  </div>
                )}
                
                {/* Source & Assigned Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 dark:bg-secondary rounded-lg">
                    <p className="text-[10px] text-gray-500 dark:text-muted-foreground uppercase font-medium">Source</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-foreground mt-0.5 capitalize">{selectedLead.source || '-'}</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-secondary rounded-lg">
                    <p className="text-[10px] text-gray-500 dark:text-muted-foreground uppercase font-medium">Assigned To</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-foreground mt-0.5">
                      {selectedLead.assignedTo ? getUserName(selectedLead.assignedTo) : 'Unassigned'}
                    </p>
                  </div>
                </div>
                
                {/* Venue if assigned */}
                {selectedLead.venueId && (
                  <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                    <p className="text-[10px] text-teal-600 uppercase font-medium flex items-center gap-1">
                      <Building className="w-3 h-3" />
                      Venue
                    </p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-foreground mt-0.5">{getVenueName(selectedLead.venueId)}</p>
                  </div>
                )}
                
                {/* Notes */}
                {selectedLead.notes && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 rounded-lg">
                    <p className="text-[10px] text-yellow-700 uppercase font-medium">Notes</p>
                    <p className="text-sm text-gray-700 dark:text-foreground mt-1 whitespace-pre-wrap">{selectedLead.notes}</p>
                  </div>
                )}
                
                {/* Status Update Section */}
                <div className="p-3 bg-slate-100 rounded-lg">
                  <p className="text-[10px] text-slate-600 uppercase font-medium mb-2">Update Status</p>
                  <Select 
                    value={selectedLead.status} 
                    onValueChange={handleStatusUpdate}
                    disabled={updatingStatus}
                  >
                    <SelectTrigger className="h-9 text-sm bg-white dark:bg-card">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">
                        <span className="flex items-center gap-2">
                          <UserPlus className="w-3.5 h-3.5 text-blue-500" />
                          New
                        </span>
                      </SelectItem>
                      <SelectItem value="contacted">
                        <span className="flex items-center gap-2">
                          <PhoneCall className="w-3.5 h-3.5 text-purple-500" />
                          Contacted
                        </span>
                      </SelectItem>
                      <SelectItem value="in_followup">
                        <span className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-yellow-500" />
                          In Followup
                        </span>
                      </SelectItem>
                      <SelectItem value="won">
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                          Won
                        </span>
                      </SelectItem>
                      <SelectItem value="lost">
                        <span className="flex items-center gap-2">
                          <XCircle className="w-3.5 h-3.5 text-red-500" />
                          Lost
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Lead Created Info */}
                <div className="p-3 bg-gray-50 dark:bg-secondary rounded-lg flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-muted-foreground uppercase">Lead Created</p>
                    <p className="text-xs text-gray-700 dark:text-foreground mt-0.5">
                      {selectedLead.createdAt ? new Date(selectedLead.createdAt).toLocaleString('en-IN', {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      }) : '-'}
                    </p>
                  </div>
                  {selectedLead.createdBy && (
                    <div className="text-right">
                      <p className="text-xs font-medium text-gray-700 dark:text-foreground">{getUserName(selectedLead.createdBy)}</p>
                      <Badge className="text-[9px] bg-gray-200 text-gray-600 dark:text-muted-foreground capitalize">
                        {getUserRole(selectedLead.createdBy)}
                      </Badge>
                    </div>
                  )}
                </div>
                
                {/* Expired Warning */}
                {isDatePassed(selectedLead.bookingDateFrom) && !['won', 'lost'].includes(selectedLead.status) && (
                  <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 rounded-lg text-xs text-red-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>Event date has passed. Consider marking as Lost.</span>
                  </div>
                )}
              </div>
              
              {/* Action Buttons Footer */}
              <div className="p-4 border-t bg-white dark:bg-card sticky bottom-0">
                <div className="flex gap-2">
                  {/* Convert to Booking */}
                  {selectedLead.status !== 'won' && (
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm h-9"
                      onClick={() => {
                        setLeadDetailOpen(false);
                        setDialogOpen(false);
                        navigate(`/bookings?fromLead=${selectedLead.id}`);
                      }}
                    >
                      <ArrowRightCircle className="w-3.5 h-3.5 mr-1" />
                      Convert to Booking
                    </Button>
                  )}
                  
                  {/* Refresh */}
                  <Button 
                    variant="outline"
                    size="sm"
                    className="h-9 px-2"
                    onClick={() => loadLeads()}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </Button>
                  
                  {/* Edit */}
                  <Button 
                    variant="outline"
                    size="sm"
                    className="h-9 px-2"
                    onClick={() => {
                      setLeadDetailOpen(false);
                      setDialogOpen(false);
                      navigate(`/leads?edit=${selectedLead.id}`);
                    }}
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  
                  {/* Delete */}
                  <Button 
                    variant="outline"
                    size="sm"
                    className="h-9 px-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:bg-red-900/20"
                    onClick={handleDeleteLead}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dates Not Sure Modal */}
      <Dialog open={datesNotSureOpen} onOpenChange={setDatesNotSureOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              Dates Not Sure
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 dark:text-muted-foreground -mt-2">
            {leadsWithoutDate.length} lead{leadsWithoutDate.length !== 1 ? 's' : ''} without booking date
          </p>
          <div className="flex-1 overflow-y-auto space-y-2 py-4">
            {leadsWithoutDate.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>All leads have booking dates</p>
              </div>
            ) : (
              leadsWithoutDate.map((lead) => {
                const statusConfig = getStatusColorText(lead.status);
                return (
                  <div
                    key={lead.id}
                    className="p-3 rounded-lg border bg-white dark:bg-card hover:bg-gray-50 dark:bg-secondary cursor-pointer transition-all"
                    onClick={() => {
                      setSelectedLead(lead);
                      setDatesNotSureOpen(false);
                      setLeadDetailOpen(true);
                    }}
                    data-testid={`no-date-lead-${lead.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900 dark:text-foreground truncate">{lead.name}</p>
                          <Badge className={`${statusConfig.bg} ${statusConfig.text} text-[10px]`}>
                            {lead.status?.replace('_', ' ').toUpperCase() || 'NEW'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-muted-foreground">{lead.phone}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-muted-foreground">
                          <span>{lead.eventType}</span>
                          {lead.followupDate && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Follow-up: {formatDate(lead.followupDate)}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 dark:text-muted-foreground" />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default MonthlyLeads;
